import { CloseIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, IconButton, Input, Menu, MenuButton, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import ModelEditor from 'components/editor/ModelEditor';
import { toPng } from 'html-to-image';
import { useEffect, useRef, useState } from 'react';
import { Editor, EditorRef, EmailEditorProps } from 'react-email-editor'; // Importa il componente EmailEditor
import { IoIosAddCircle, IoIosArrowDown } from 'react-icons/io';
import { MdDocumentScanner } from "react-icons/md";
import { toast } from "react-toastify";
import { createModel } from 'services/ModelService';
import { truncateName } from 'utils/common-utils';
import { GetModelTypeRs } from 'utils/interfaces';
import modelTemplateBoilerplate from '../../../../utils/editor/model-template-boilerplate.json';

const NewModelModal = (
    { isOpen, onClose, modelTypes }: { isOpen: boolean; onClose: () => void, modelTypes: GetModelTypeRs[] }
) => {
    const [modelName, setModelName] = useState<string>("");
    const [modelTypeId, setModelTypeId] = useState<number>();
    const [showEditor, setShowEditor] = useState(false); // Stato per visualizzare l'editor
    const [isModelCreated, setIsModelCreated] = useState(false); // Stato per tenere traccia se il modello è stato creato
    const [loading, setLoading] = useState<boolean>(false); // Stato per il caricamento
    const [editorLoading, setEditorLoading] = useState<boolean>(false); // Stato per il caricamento nella modale dell'editor

    // MODEL DATA
    const emailEditorRef = useRef<EditorRef>(null);  // Riferimento all'editor
    const [coverImg, setCoverImg] = useState<Uint8Array>();
    const [html, setHtml] = useState<string>("");
    const [jsonDesign, setJsonDesign] = useState<any>("");

    const { keycloak } = useKeycloak();

    useEffect(() => {
        if (!isOpen) {
            setModelName("");
            setModelTypeId(undefined);
            setShowEditor(false); // Nasconde l'editor quando la modale è chiusa
            setCoverImg(undefined);
            setHtml("");
            setJsonDesign("");
        }
    }, [isOpen]);

    const onLoad: EmailEditorProps['onLoad'] = (unlayer: Editor) => {
        unlayer.loadDesign(jsonDesign ? jsonDesign : modelTemplateBoilerplate);
    };

    const generateModel = async () => {
        setEditorLoading(true); // Imposta lo stato di caricamento su true per la modale dell'editor
        const unlayer: Editor = emailEditorRef.current?.editor;
        unlayer?.exportHtml(async (data) => {
            const { design, html } = data;

            // Converte l'HTML in immagine
            const node = document.createElement('div');
            node.innerHTML = html;
            document.body.appendChild(node);

            try {
                const dataUrl = await toPng(node, {
                    cacheBust: true,
                    filter: (domNode) => {
                        return !(domNode.tagName === 'IMG' || domNode.tagName === 'VIDEO');
                    }
                });
                const byteString = atob(dataUrl.split(',')[1]);
                const coverImg = new Uint8Array(byteString.length);
                for (let i = 0; i < byteString.length; i++) {
                    coverImg[i] = byteString.charCodeAt(i);
                }

                setCoverImg(coverImg);
                setHtml(html);
                setJsonDesign(design);
                setShowEditor(false);
                setIsModelCreated(true);
            } catch (error) {
                toast.error("Errore durante la creazione dell'immagine del modello", {
                    position: "bottom-right"
                });
            } finally {
                document.body.removeChild(node);
                setEditorLoading(false); // Imposta lo stato di caricamento su false per la modale dell'editor
            }
        });
    };

    const createModelOnBackend = async () => {
        setLoading(true); // Imposta lo stato di caricamento su true
        try {
            // Converte Uint8Array in Blob e Crea un File a partire dal Blob
            const blob = new Blob([coverImg], { type: 'image/png' });
            const file = new File([blob], 'cover.png', { type: 'image/png' });

            await createModel(
                keycloak.token,
                onSessionExpired,
                modelName,
                modelTypeId,
                html,
                JSON.stringify(jsonDesign),
                file
            );

            toast.success("Modello creato con successo", {
                position: "bottom-right"
            });

            await resetModelData();

            onClose();
        } catch (error) {
            toast.error("Errore durante la creazione del modello.", {
                position: "bottom-right"
            });
        } finally {
            setLoading(false); // Imposta lo stato di caricamento su false
        }
    };

    const cancelModelCreation = async () => {
        setShowEditor(false);
        setIsModelCreated(false);
        await resetModelData();

        onClose();
    };

    const onSessionExpired = () => {
        toast.error("Sessione scaduta. Effettua nuovamente l'accesso.", {
            position: "bottom-right",
            onClose: () => {
                keycloak.logout();
                localStorage.removeItem("kc-token");
            }
        });
    };

    const resetModelData = async () => {
        setModelName("");
        setModelTypeId(undefined);
        setCoverImg(undefined);
        setHtml("");
        setJsonDesign("");
        setIsModelCreated(false);
    }

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader backgroundColor="white" color="#9FB50C" zIndex={1} textAlign="left">
                        Crea Modello
                    </ModalHeader>
                    <ModalCloseButton _focus={{ boxShadow: 'none' }} />
                    <ModalBody>
                        <Text fontWeight="bold">Nome modello <Text as="span" color="red">*</Text></Text>
                        <Input
                            value={modelName}
                            onChange={(e) => setModelName(e.target.value)}
                            placeholder="Nome modello"
                        />
                        <Box flex="1" mt={4}>
                            <Text fontWeight="bold">
                                Tipo Modello
                                <Text as="span" color="red">*</Text>
                            </Text>
                            <Menu>
                                <MenuButton
                                    as={Button}
                                    rightIcon={<IoIosArrowDown />}
                                    variant="subtle"
                                    color="var(--chakra-colors-secondaryGray-600)"
                                    textAlign="left"
                                    fontWeight="normal"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="0.375rem"
                                    width="100%"
                                >
                                    {modelTypes ? (truncateName(modelTypes.find((type) => type.id === modelTypeId)?.type || "Tipo Modello")) : "Tipo Modello"}
                                </MenuButton>
                                <MenuList textAlign="left" maxHeight="220px" overflowY="auto" zIndex="popover">
                                    {modelTypes.map((modelType) => (
                                        <MenuItem _hover={{ bg: 'gray.200' }} key={modelType.id} onClick={() => setModelTypeId(modelType.id)}>
                                            {modelType.type}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </Menu>
                        </Box>
                        <Text fontWeight="bold" mt={4}>
                            Contenuto Modello
                            <Text as="span" color="red">*</Text>
                        </Text>
                        <Button
                            colorScheme={"#9FB50C"}
                            backgroundColor={'#9FB50C'}
                            color="white"
                            minWidth="100px"
                            leftIcon={isModelCreated ? <MdDocumentScanner /> : <IoIosAddCircle />}
                            onClick={() => setShowEditor(true)}  // Apre editor con modello "draft"
                        >
                            {isModelCreated ? "Modifica" : "Crea"}
                        </Button>
                    </ModalBody>
                    <ModalFooter backgroundColor="white" zIndex={1}>
                        <Tooltip
                            label="Completa tutti i dati obbligatori per salvare"
                            isDisabled={modelName || modelTypeId || html || jsonDesign}
                            shouldWrapChildren
                        >
                            <Button
                                colorScheme={"#688CC8"}
                                backgroundColor={'#688CC8'}
                                color="white"
                                mr={3}
                                onClick={createModelOnBackend}
                                disabled={!modelName || !modelTypeId || !html || !jsonDesign || loading}
                                isLoading={loading} // Mostra lo spinner durante il caricamento
                                loadingText="Salva"
                            >
                                {loading ? <Spinner size="sm" mr={2} /> : null}
                                Salva
                            </Button>
                        </Tooltip>
                        <Button
                            textColor={"gray.900"}
                            colorScheme="gray"
                            onClick={cancelModelCreation}
                            disabled={loading} // Disabilita il pulsante durante il caricamento
                        >
                            Indietro
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Modal for Model Editor */}
            <Modal
                isOpen={showEditor}
                onClose={() => setShowEditor(false)}
            >
                <ModalOverlay />
                <ModalContent maxWidth="95%" mt="15px">
                    <ModalHeader backgroundColor="white" color="#9FB50C" zIndex={1} textAlign="left">
                        Editor Modello
                        <IconButton
                            aria-label="Close modal"
                            icon={<CloseIcon />}
                            position="absolute"
                            top="8px"
                            right="8px"
                            onClick={() => setShowEditor(false)}
                            disabled={editorLoading} // Disabilita il pulsante durante il caricamento
                            color="grey"
                        />
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <ModelEditor
                            emailEditorRef={emailEditorRef}
                            onLoad={onLoad}
                        />
                        <Flex justifyContent={"space-between"} alignItems={"center"} mt={5}>
                            <Flex>
                                <Text fontSize="sm">
                                    <Text as="span" fontWeight="bold" color={"#9FB50C"}>Etichette</Text>
                                    <br />
                                    <Text as="span" fontWeight="bold">Tutti i Modelli:</Text> [nome_cliente]
                                    <br />
                                    <Text as="span" fontWeight="bold">Modelli Scadenze:</Text> [categoria_scadenza] [tipologia_scadenza] [data_scadenza]
                                </Text>
                            </Flex>
                            <Flex>
                                <Button
                                    colorScheme={"#688CC8"}
                                    backgroundColor={'#688CC8'}
                                    color="white"
                                    mr={3}
                                    onClick={generateModel}
                                    disabled={editorLoading} // Disabilita il pulsante durante il caricamento
                                    isLoading={editorLoading} // Mostra lo spinner durante il caricamento
                                    loadingText="Salva"
                                >
                                    {editorLoading ? <Spinner size="sm" mr={2} /> : null}
                                    Salva
                                </Button>
                                <Button
                                    textColor={"gray.900"}
                                    colorScheme="gray"
                                    onClick={() => setShowEditor(false)}
                                    disabled={editorLoading} // Disabilita il pulsante durante il caricamento
                                >
                                    Indietro
                                </Button>
                            </Flex>
                        </Flex>
                    </ModalBody>
                </ModalContent>
            </Modal>

        </>
    );
};

export default NewModelModal;